import * as Types from '@trello/graphql/generated';

import { useQuickLoad } from '@trello/quickload';
import type { TrelloQueryHookOptions, TrelloLazyQueryHookOptions, TrelloSuspenseQueryHookOptions } from '@trello/quickload';
import DocumentNode from '@trello/graphql/src/documentNode';
import * as Apollo from '@apollo/client';
export const InvitationTokenPageDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"InvitationTokenPage"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"orgId"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"ID"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"organization"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"id"},"value":{"kind":"Variable","name":{"kind":"Name","value":"orgId"}}}],"directives":[{"kind":"Directive","name":{"kind":"Name","value":"client"}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"displayName"}},{"kind":"Field","name":{"kind":"Name","value":"members"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"fullName"}},{"kind":"Field","name":{"kind":"Name","value":"nonPublic"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"fullName"}}]}}]}},{"kind":"Field","name":{"kind":"Name","value":"name"}},{"kind":"Field","name":{"kind":"Name","value":"prefs"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"orgInviteRestrict"}}]}}]}}]}}]} as unknown as DocumentNode;
const defaultOptions = {"context":{"operationName":"InvitationTokenPage","document":InvitationTokenPageDocument}} as const;
export type InvitationTokenPageQueryVariables = Types.Exact<{
  orgId: Types.Scalars['ID']['input'];
}>;


export type InvitationTokenPageQuery = (
  { __typename: 'Query' }
  & { organization?: Types.Maybe<(
    { __typename: 'Organization' }
    & Pick<Types.Organization, 'id' | 'displayName' | 'name'>
    & { members: Array<(
      { __typename: 'Member' }
      & Pick<Types.Member, 'id' | 'fullName'>
      & { nonPublic?: Types.Maybe<(
        { __typename: 'Member_NonPublic' }
        & Pick<Types.Member_NonPublic, 'fullName'>
      )> }
    )>, prefs: (
      { __typename: 'Organization_Prefs' }
      & Pick<Types.Organization_Prefs, 'orgInviteRestrict'>
    ) }
  )> }
);

/**
 * __useInvitationTokenPageQuery__
 *
 * To run a query within a React component, call `useInvitationTokenPageQuery` and pass it any options that fit your needs.
 * When your component renders, `useInvitationTokenPageQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useInvitationTokenPageQuery({
 *   variables: {
 *      orgId: // value for 'orgId'
 *   },
 * });
 */
export function useInvitationTokenPageQuery(
  baseOptions: TrelloQueryHookOptions<
    InvitationTokenPageQuery,
    InvitationTokenPageQueryVariables
  > &
    (
      | { variables: InvitationTokenPageQueryVariables; skip?: boolean }
      | { skip: boolean }
    ),
) {
  const isQuickLoading = useQuickLoad({
    waitOn: baseOptions?.waitOn || ['None'],
    document: InvitationTokenPageDocument,
    skip: baseOptions?.skip,
  });
  const options = {
    ...defaultOptions,
    ...baseOptions,
    skip: isQuickLoading || baseOptions?.skip,
  };
  const result = Apollo.useQuery<
    InvitationTokenPageQuery,
    InvitationTokenPageQueryVariables
  >(InvitationTokenPageDocument, options);
  // reconstruct result because modifying the useQuery result actually changes apollo behavior because of memoization
  const trelloResult = { ...result };
  if (isQuickLoading) {
    trelloResult.loading = true;
  }

  return trelloResult;
}
export function useInvitationTokenPageLazyQuery(
  baseOptions?: TrelloLazyQueryHookOptions<
    InvitationTokenPageQuery,
    InvitationTokenPageQueryVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<
    InvitationTokenPageQuery,
    InvitationTokenPageQueryVariables
  >(InvitationTokenPageDocument, options);
}
export function useInvitationTokenPageSuspenseQuery(
  baseOptions?:
    | Apollo.SkipToken
    | TrelloSuspenseQueryHookOptions<
        InvitationTokenPageQuery,
        InvitationTokenPageQueryVariables
      >,
) {
  const options =
    baseOptions === Apollo.skipToken
      ? baseOptions
      : { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<
    InvitationTokenPageQuery,
    InvitationTokenPageQueryVariables
  >(InvitationTokenPageDocument, options);
}
export type InvitationTokenPageQueryHookResult = ReturnType<
  typeof useInvitationTokenPageQuery
>;
export type InvitationTokenPageLazyQueryHookResult = ReturnType<
  typeof useInvitationTokenPageLazyQuery
>;
export type InvitationTokenPageSuspenseQueryHookResult = ReturnType<
  typeof useInvitationTokenPageSuspenseQuery
>;
export type InvitationTokenPageQueryResult = Apollo.QueryResult<
  InvitationTokenPageQuery,
  InvitationTokenPageQueryVariables
>;
